// ─── AUTH SCREEN ──────────────────────────────────────────────────────────────
// Full-screen gate for unauthenticated users. Sign in · Sign up · Reset.
// Matches the terminal design. Never renders terminal content.

import { useState } from "react";

import { backendFetch, C, font, s } from "./core";
import { sendPasswordReset, signInWithPassword, signUpWithPassword, supabase } from "./supabase";

const GOLD = "#FFD700";

function Field({ label, type = "text", value, onChange, placeholder, autoFocus }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: C.amber, letterSpacing: "0.1em", fontWeight: 700, marginBottom: 4 }}>
        {label}
      </div>
      <input
        style={s.inp}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="off"
        autoFocus={autoFocus}
      />
    </div>
  );
}

function LinkBtn({ onClick, children }) {
  return (
    <span
      onClick={onClick}
      style={{ color: C.amber, cursor: "pointer", fontWeight: 700 }}
    >
      {children}
    </span>
  );
}

export function AuthScreen() {
  const [mode, setMode] = useState("signin"); // signin | signup | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  const reset = () => { setErr(""); setInfo(""); };
  const go = (m) => { reset(); setMode(m); };

  const handleSignin = async () => {
    reset(); setBusy(true);
    try {
      const { error } = await signInWithPassword(email.trim(), password);
      if (error) { setErr(error.message); setBusy(false); return; }
      // AuthProvider's onAuthStateChange takes over from here.
    } catch (e) {
      setErr(e.message || "Sign in failed."); setBusy(false);
    }
  };

  const handleSignup = async () => {
    reset();
    if (password !== confirm) { setErr("Passwords do not match."); return; }
    if (password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    setBusy(true);
    try {
      const { data, error } = await signUpWithPassword(email.trim(), password);
      if (error) { setErr(error.message); setBusy(false); return; }

      const session = data?.session;
      if (!session?.access_token) {
        // Email confirmation is enabled — no session yet.
        setInfo("Account created. Check your email to confirm, then sign in.");
        setMode("signin"); setBusy(false); return;
      }

      const token = session.access_token;
      localStorage.setItem("qs_auth_token", token);

      // Create the backend user + start the trial subscription.
      try {
        await backendFetch("https://api", "/auth/verify", {
          method: "POST",
          body: JSON.stringify({ token }),
        });
      } catch {}

      // Redirect to Stripe to collect a card (no charge during the trial).
      try {
        const res = await backendFetch("https://api", "/subscriptions/checkout", {
          method: "POST",
          body: JSON.stringify({ tier: "pro_supplied" }),
        });
        if (res.ok) {
          const body = await res.json();
          if (body.checkout_url) { window.location.href = body.checkout_url; return; }
        }
      } catch {}

      // Checkout unavailable → AuthProvider will drop us into the terminal.
      setBusy(false);
    } catch (e) {
      setErr(e.message || "Sign up failed."); setBusy(false);
    }
  };

  const handleReset = async () => {
    reset(); setBusy(true);
    try {
      const { error } = await sendPasswordReset(email.trim());
      if (error) setErr(error.message);
      else setInfo("Password reset email sent. Check your inbox.");
    } catch (e) {
      setErr(e.message || "Could not send reset email.");
    }
    setBusy(false);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (busy) return;
    // Auth backend unconfigured (missing env vars) — keep the form visible but
    // surface a friendly error instead of attempting a doomed request.
    if (!supabase) {
      reset();
      setErr("Service temporarily unavailable. Please try again shortly.");
      return;
    }
    if (mode === "signin") handleSignin();
    else if (mode === "signup") handleSignup();
    else handleReset();
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: C.bg,
      fontFamily: font,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
    }}>
      <form onSubmit={onSubmit} style={{
        width: "min(420px, 94vw)",
        background: C.panel,
        border: `1px solid ${C.border}`,
        borderRadius: 4,
        padding: 28,
      }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ color: C.amber, fontWeight: 700, letterSpacing: "0.2em" }}>
            <span style={{ fontSize: 30 }}>◈</span>{" "}
            <span style={{ fontSize: 18 }}>QUANT-SIGNAL</span>
          </div>
          <div style={{ fontSize: 10, color: C.textDim, letterSpacing: "0.35em", marginTop: 4 }}>
            NOUS TOPOLOGY
          </div>
        </div>

        {mode === "signin" && (
          <>
            <Field label="EMAIL" type="email" value={email} onChange={setEmail} placeholder="you@example.com" autoFocus />
            <Field label="PASSWORD" type="password" value={password} onChange={setPassword} placeholder="••••••••" />
          </>
        )}

        {mode === "signup" && (
          <>
            <Field label="EMAIL" type="email" value={email} onChange={setEmail} placeholder="you@example.com" autoFocus />
            <Field label="PASSWORD" type="password" value={password} onChange={setPassword} placeholder="At least 6 characters" />
            <Field label="CONFIRM PASSWORD" type="password" value={confirm} onChange={setConfirm} placeholder="••••••••" />
            <div style={{ color: GOLD, fontWeight: 700, fontSize: 11, lineHeight: 1.6, marginBottom: 12 }}>
              30-day trial · Card required after trial · Auto-renews at $20/month · Cancel anytime
            </div>
          </>
        )}

        {mode === "reset" && (
          <Field label="EMAIL" type="email" value={email} onChange={setEmail} placeholder="you@example.com" autoFocus />
        )}

        {err && (
          <div style={{ color: "#FF5252", fontSize: 12, marginBottom: 10, lineHeight: 1.5 }}>{err}</div>
        )}
        {info && (
          <div style={{ color: C.green, fontSize: 12, marginBottom: 10, lineHeight: 1.5 }}>{info}</div>
        )}

        <button
          type="submit"
          disabled={busy}
          style={{ ...s.btn("amber"), width: "100%", padding: "10px 0", fontSize: 14, opacity: busy ? 0.6 : 1, cursor: busy ? "wait" : "pointer" }}
        >
          {busy
            ? "…"
            : mode === "signin" ? "SIGN IN"
            : mode === "signup" ? "CREATE ACCOUNT"
            : "SEND RESET EMAIL"}
        </button>

        {/* Mode switch links */}
        <div style={{ fontSize: 12, color: C.textDim, textAlign: "center", marginTop: 14, lineHeight: 1.9 }}>
          {mode === "signin" && (
            <>
              <div>New here? <LinkBtn onClick={() => go("signup")}>Create an account</LinkBtn></div>
              <div><LinkBtn onClick={() => go("reset")}>Forgot your password?</LinkBtn></div>
            </>
          )}
          {mode === "signup" && (
            <div>Already have an account? <LinkBtn onClick={() => go("signin")}>Sign in</LinkBtn></div>
          )}
          {mode === "reset" && (
            <div><LinkBtn onClick={() => go("signin")}>Back to sign in</LinkBtn></div>
          )}
        </div>
      </form>

      {/* Academic / library access */}
      <div style={{ fontSize: 11, color: C.textDim, marginTop: 16, textAlign: "center", lineHeight: 1.6 }}>
        Academic or library access?{" "}
        <a
          href="mailto:education@quant-signal.ai?subject=Education%20tier%20access"
          style={{ color: C.textMid, textDecoration: "none" }}
        >
          Inquire about the Education tier
        </a>
      </div>
    </div>
  );
}
